import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";

// Default Leaflet markers don't load with create-react-app's bundler; we
// rebuild the icon inline so it just works with the OSM tiles.
const goldIcon = new L.DivIcon({
  className: "custom-pin",
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#d4af37;border:3px solid #0a0a0a;box-shadow:0 0 12px rgba(212,175,55,.7)"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const driverIcon = new L.DivIcon({
  className: "custom-pin",
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#34d399;border:3px solid #0a0a0a;box-shadow:0 0 16px rgba(52,211,153,.8);display:flex;align-items:center;justify-content:center;color:#0a0a0a;font-size:12px;font-weight:bold">🚗</div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const dropoffIcon = new L.DivIcon({
  className: "custom-pin",
  html: '<div style="width:18px;height:18px;border-radius:3px;background:#ef4444;border:3px solid #0a0a0a;box-shadow:0 0 12px rgba(239,68,68,.6)"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const waypointIcon = new L.DivIcon({
  className: "custom-pin",
  html: '<div style="width:22px;height:22px;border-radius:50%;background:#7c3aed;border:3px solid #0a0a0a;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;box-shadow:0 0 12px rgba(124,58,237,.7)">•</div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], map.getZoom(), { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

// Worldwide-default center (zoomed out — user's geolocation will jump it
// to wherever they are).
const WORLD_FALLBACK = { lat: 20, lng: 0 };

// Treat (0,0) and missing coords as "no pin" — both Null Island sentinels
// our forms emit before the user picks a location.
const hasCoords = (p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && !(p.lat === 0 && p.lng === 0);

/**
 * AutoFit — keeps the map framed on the user's current pickup/dropoff.
 * Single pin: pan + zoom to it (zoom=14, neighbourhood-level).
 * Both pins: fitBounds with padding so both are visible.
 * No pins:   leave the user-controlled view alone.
 */
function AutoFit({ pickup, dropoff }) {
  const map = useMap();
  useEffect(() => {
    const p = hasCoords(pickup) ? pickup : null;
    const d = hasCoords(dropoff) ? dropoff : null;
    if (p && d) {
      const bounds = L.latLngBounds([p.lat, p.lng], [d.lat, d.lng]);
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15, animate: true });
    } else if (p || d) {
      const target = p || d;
      map.setView([target.lat, target.lng], 14, { animate: true });
    }
  }, [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng, map]);
  return null;
}

export default function MapView({
  pickup, dropoff, driver, waypoints, height = 360, recenter, onClick, children, "data-testid": tid,
  initialZoom = 13,
}) {
  // Pick the most-specific known location; if none, render a wide world view
  // so the user can pan to any continent.
  const real = hasCoords(pickup) ? pickup : hasCoords(dropoff) ? dropoff : hasCoords(driver) ? driver : null;
  const center = real || WORLD_FALLBACK;
  const zoom = real ? initialZoom : 2;
  return (
    <div data-testid={tid} className="rounded-md overflow-hidden border border-gold/20" style={{ height }}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        worldCopyJump={true}
        minZoom={2}
        style={{ height: "100%", width: "100%", background: "#0a0a0a" }}
        whenReady={(e) => { if (onClick) e.target.on("click", (ev) => onClick(ev.latlng)); }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          noWrap={false}
        />
        {hasCoords(pickup) && (
          <Marker position={[pickup.lat, pickup.lng]} icon={goldIcon}>
            <Popup>Pickup<br/>{pickup.address || ""}</Popup>
          </Marker>
        )}
        {(waypoints || []).filter(hasCoords).map((w, idx) => (
          <Marker key={w.id || idx} position={[w.lat, w.lng]} icon={waypointIcon}>
            <Popup>Stop {idx + 1}<br/>{w.address || ""}</Popup>
          </Marker>
        ))}
        {hasCoords(dropoff) && (
          <Marker position={[dropoff.lat, dropoff.lng]} icon={dropoffIcon}>
            <Popup>Drop-off<br/>{dropoff.address || ""}</Popup>
          </Marker>
        )}
        {hasCoords(driver) && (
          <Marker position={[driver.lat, driver.lng]} icon={driverIcon}>
            <Popup>Your driver</Popup>
          </Marker>
        )}
        {children}
        {recenter && <Recenter lat={recenter.lat} lng={recenter.lng} />}
        <AutoFit pickup={pickup} dropoff={dropoff} />
      </MapContainer>
    </div>
  );
}
